// src/components/SafeImage.tsx
import React, { useState } from 'react';

interface SafeImageProps {
  src: string;
  alt: string;
  fallback: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * SafeImage component that gracefully handles 404/broken images
 * by falling back to a provided placeholder component.
 *
 * Usage:
 * <SafeImage
 *   src={imageUrl}
 *   alt="Description"
 *   fallback={<div>Placeholder</div>}
 *   className="my-image"
 *   style={{ width: 32, height: 32 }}
 * />
 */
export const SafeImage: React.FC<SafeImageProps> = ({
  src,
  alt,
  fallback,
  className,
  style
}) => {
  const [hasError, setHasError] = useState(false);

  // If error occurred or no src provided, show fallback
  if (hasError || !src || src === '—') {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};
